import EmojiFlagsIcon from '@mui/icons-material/EmojiFlags';
import InfoIcon from '@mui/icons-material/Info';
import LanguageIcon from '@mui/icons-material/Language';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Button, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router';
import generatePDF from 'react-to-pdf';

import pitchbook from '~/assets/icons/pitchbook.svg';
import { CompanyHistoryChanges } from '~/components/CompanyHistoryChanges';
import { ConfirmModal } from '~/components/ConfirmModal';
import { FullScreenLoader } from '~/components/FullScreenLoader';
import { StatusSteps } from '~/components/StatusSteps';
import { ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import {
  useDeleteCompanyMutation,
  useGetCompanyQuery,
  useUpdateCompanyStatusMutation,
} from '~/services/rtkQuery/company';
import { CompanyStatus } from '~/types/models';
import { formatUrl } from '~/utils/formats';
import { getApiError } from '~/utils/helpers';

import { Analytics } from './components/Analytics';
import { GeneralData } from './components/GeneralData';
import { Inference } from './components/Inference';
import styles from './styles.module.scss';

type ACTION_TYPE = 'delete' | 'review' | 'reject' | 'close';
type TTab = 'general-data' | 'inference' | 'analytics' | 'history';

const TABS: TTab[] = ['general-data', 'inference', 'analytics', 'history'];

export const CompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') as TTab | null;
  const params = useParams();
  const companyId = params.id ? +params.id : null;
  const { handleApiError, showNotification } = useNotification();
  const getCompanyQuery = useGetCompanyQuery({ id: companyId || 0 }, { skip: !companyId });
  const [deleteCompanyMutation, { isLoading: isLoadingDeleteCompanyMutation }] =
    useDeleteCompanyMutation();
  const [updateCompanyStatusMutation, { isLoading: isLoadingUpdateCompanyStatusMutation }] =
    useUpdateCompanyStatusMutation();
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const [actionConfirmModalVisible, setActionConfirmModalVisible] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const actionType = useRef<ACTION_TYPE | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tabIndex = TABS.findIndex(tab => tab === activeTab);

    if (tabIndex === -1) {
      setActiveTabIndex(1);
    } else {
      setActiveTabIndex(tabIndex);
    }
  }, [activeTab]);

  useEffect(() => {
    if (getCompanyQuery.error) {
      const apiError = getApiError(getCompanyQuery.error);

      if (apiError) {
        navigate(ROUTES.NOT_FOUND, { replace: true });
      }
    }
  }, [getCompanyQuery, navigate]);

  const handleActiveTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setSearchParams({ tab: TABS[newValue] });
    },
    [setSearchParams],
  );

  const handlePdfDownload = useCallback(async () => {
    try {
      setGeneratingPdf(true);

      showNotification({
        type: 'info',
        title: 'Download Report',
        message: 'Downloading..Please wait.',
      });

      await new Promise(resolve => {
        setTimeout(resolve, 5000);
      });

      await generatePDF(containerRef, {
        filename: `${getCompanyQuery.data?.result.data.name} ${
          activeTabIndex === 1 ? 'Inference' : 'Analytics'
        }.pdf`,
      });

      showNotification({
        type: 'success',
        title: 'Download Report',
        message: 'Download Complete',
      });
    } catch (e) {
      showNotification({
        type: 'error',
        title: 'Download Report',
        message: 'Download failed',
      });
    } finally {
      setGeneratingPdf(false);
    }
  }, [activeTabIndex, getCompanyQuery.data?.result.data.name, showNotification]);

  const showActionConfirmModal = useCallback(() => {
    setActionConfirmModalVisible(true);
  }, []);

  const hideActionConfirmModal = useCallback(() => {
    setActionConfirmModalVisible(false);
  }, []);

  const handleActionButtonClick = useCallback(
    (type: ACTION_TYPE) => {
      actionType.current = type;

      showActionConfirmModal();
    },
    [showActionConfirmModal],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (companyId) {
        const response = await deleteCompanyMutation({
          id: companyId,
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });

        navigate(ROUTES.COMPANIES);
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [companyId, deleteCompanyMutation, handleApiError, navigate, showNotification]);

  const handleReview = useCallback(async () => {
    try {
      if (companyId) {
        const response = await updateCompanyStatusMutation({
          id: companyId,
          status: 'draft',
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });

        navigate(generatePath(ROUTES.EDIT_COMPANY, { id: response.result.data.id.toString() }));
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [companyId, handleApiError, navigate, showNotification, updateCompanyStatusMutation]);

  const handleCompanyStatusChange = useCallback(
    async (status: CompanyStatus) => {
      try {
        if (companyId) {
          const response = await updateCompanyStatusMutation({ id: companyId, status }).unwrap();

          showNotification({
            type: 'success',
            title: response.title,
            message: response.message,
          });

          navigate(ROUTES.COMPANIES);
        }
      } catch (e) {
        handleApiError(e);
      }
    },
    [companyId, handleApiError, navigate, showNotification, updateCompanyStatusMutation],
  );

  const handleActionConfirm = useCallback(async () => {
    if (actionType.current === 'delete') {
      await handleDelete();
    }

    if (actionType.current === 'review') {
      await handleReview();
    }

    if (actionType.current === 'reject') {
      await handleCompanyStatusChange('rejected');
    }

    if (actionType.current === 'close') {
      await handleCompanyStatusChange('closed');
    }

    hideActionConfirmModal();
  }, [handleCompanyStatusChange, handleDelete, handleReview, hideActionConfirmModal]);

  const dealSize = useMemo(() => {
    return {
      a: Number(getCompanyQuery.data?.result.data.rounds[0].summary.total_deal_size),
      b: Number(getCompanyQuery.data?.result.data.rounds[1].summary.total_deal_size),
      c: Number(getCompanyQuery.data?.result.data.rounds[2].summary.total_deal_size),
    };
  }, [getCompanyQuery.data?.result.data.rounds]);

  const preMoney = useMemo(() => {
    return {
      a: Number(getCompanyQuery.data?.result.data.rounds[0].summary.pre_money),
      b: Number(getCompanyQuery.data?.result.data.rounds[1].summary.pre_money),
      c: Number(getCompanyQuery.data?.result.data.rounds[2].summary.pre_money),
    };
  }, [getCompanyQuery.data?.result.data.rounds]);

  const shareSold = useMemo(() => {
    return {
      a: Number(getCompanyQuery.data?.result.data.rounds[0].summary.share_sold),
      b: Number(getCompanyQuery.data?.result.data.rounds[1].summary.share_sold),
      c: Number(getCompanyQuery.data?.result.data.rounds[2].summary.share_sold),
    };
  }, [getCompanyQuery.data?.result.data.rounds]);

  return (
    <>
      <div
        className={clsx(styles.container, {
          [styles['container--loading']]: getCompanyQuery.isLoading,
          [styles['container--generating-pdf']]: generatingPdf,
        })}
        ref={containerRef}
      >
        {getCompanyQuery.isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <div className={styles.overview}>
              <div className={styles.overview__status}>
                <Typography
                  color="var(--text-3-color)"
                  maxWidth="40%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  variant="h4"
                  whiteSpace="nowrap"
                >
                  {getCompanyQuery.data?.result.data.name}
                </Typography>
                {getCompanyQuery.data?.status && !generatingPdf && (
                  <StatusSteps activeStatus={getCompanyQuery.data.result.data.status} />
                )}
              </div>
              <div className={styles.overview__info}>
                <div className={styles['overview__info__item']}>
                  <EmojiFlagsIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      width: '20px',
                      height: '20px',
                      mr: '8px',
                    }}
                  />
                  <Typography
                    alignItems="center"
                    color="var(--text-3-color)"
                    display="flex"
                    variant="subtitle2"
                  >
                    Founded
                    <Typography
                      component="span"
                      fontWeight="600"
                      maxWidth="50px"
                      ml="5px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      variant="subtitle2"
                      whiteSpace="nowrap"
                    >
                      {getCompanyQuery.data?.result.data.year_founded}
                    </Typography>
                  </Typography>
                </div>
                <div className={styles['overview__info__item']}>
                  <PersonAddAltIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      width: '20px',
                      height: '20px',
                      mr: '8px',
                    }}
                  />
                  <Typography
                    alignItems="center"
                    color="var(--text-3-color)"
                    display="flex"
                    variant="subtitle2"
                  >
                    <Typography
                      component="span"
                      fontWeight="600"
                      maxWidth="100px"
                      mr="5px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      variant="subtitle2"
                      whiteSpace="nowrap"
                    >
                      {
                        getCompanyQuery.data?.result.data.rounds[2].sub_rounds[
                          (getCompanyQuery.data?.result.data.rounds[2].sub_rounds.length || 1) - 1
                        ].employees
                      }
                    </Typography>
                    Employees
                  </Typography>
                </div>
                <div
                  className={clsx(styles['overview__info__item'], styles['overview__info--bio'])}
                >
                  <InfoIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      width: '20px',
                      height: '20px',
                      mr: '8px',
                    }}
                  />
                  <Typography
                    alignItems="center"
                    color="var(--text-3-color)"
                    display="flex"
                    overflow="hidden"
                    variant="subtitle2"
                  >
                    Description
                    <Typography
                      component="span"
                      fontWeight="600"
                      ml="5px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={getCompanyQuery.data?.result.data.bio}
                      variant="subtitle2"
                      whiteSpace="nowrap"
                    >
                      {getCompanyQuery.data?.result.data.bio || '-'}
                    </Typography>
                  </Typography>
                </div>
              </div>
              <div className={clsx(styles.overview__info, styles['overview__info--websites'])}>
                <div className={styles['overview__info__item']}>
                  <LanguageIcon
                    sx={{
                      color: 'var(--gray-6-color)',
                      width: '20px',
                      height: '20px',
                      mr: '8px',
                    }}
                  />
                  <Link
                    color="rgb(var(--main-2-color-rgb))"
                    fontWeight="500"
                    href={formatUrl(getCompanyQuery.data?.result.data.website_url)}
                    overflow="hidden"
                    target="_blank"
                    textOverflow="ellipsis"
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {getCompanyQuery.data?.result.data.website_url}
                  </Link>
                </div>
                <div className={styles['overview__info__item']}>
                  <img alt="pitchbook" src={pitchbook} />
                  {getCompanyQuery.data?.result.data.pitchbook_url ? (
                    <Link
                      color="rgb(var(--main-2-color-rgb))"
                      fontWeight="500"
                      href={formatUrl(getCompanyQuery.data.result.data.pitchbook_url)}
                      ml="8px"
                      overflow="hidden"
                      target="_blank"
                      textOverflow="ellipsis"
                      variant="subtitle2"
                      whiteSpace="nowrap"
                    >
                      {getCompanyQuery.data.result.data.pitchbook_url}
                    </Link>
                  ) : (
                    <Typography fontWeight="600" variant="subtitle2">
                      -
                    </Typography>
                  )}
                </div>
              </div>
              {(activeTabIndex === 1 || activeTabIndex === 2) && !generatingPdf && (
                <Button
                  size="small"
                  startIcon={<PictureAsPdfOutlinedIcon sx={{ width: '24px', height: '24px' }} />}
                  sx={{ width: '173px', alignSelf: 'flex-end' }}
                  onClick={handlePdfDownload}
                >
                  Download
                </Button>
              )}
              {!generatingPdf && (
                <Tabs
                  indicatorColor="primary"
                  value={activeTabIndex === null ? false : activeTabIndex}
                  onChange={handleActiveTabChange}
                >
                  <Tab label="General Data" />
                  <Tab label="Inference" />
                  <Tab label="Analytics" />
                  <Tab label="History" />
                </Tabs>
              )}
            </div>
            <div className={styles['tab-content']}>
              {activeTabIndex === 0 && getCompanyQuery.data && (
                <GeneralData data={getCompanyQuery.data.result.data} />
              )}
              {activeTabIndex === 1 && companyId && (
                <Inference companyId={companyId} generatingPdf={generatingPdf} />
              )}
              {activeTabIndex === 2 && getCompanyQuery.data && (
                <Analytics dealSize={dealSize} preMoney={preMoney} shareSold={shareSold} />
              )}
              {activeTabIndex === 3 && companyId && <CompanyHistoryChanges companyId={companyId} />}
              {!generatingPdf && (
                <div className={styles['tab-content__buttons']}>
                  {getCompanyQuery.data?.result.data.status !== 'in_progress' && (
                    <Button
                      color="secondary"
                      size="small"
                      sx={{
                        width: '158px',
                        mr: '16px',
                        fontSize: '14px',
                        lineHeight: 'normal',
                      }}
                      variant="outlined"
                      onClick={() => handleActionButtonClick('delete')}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    color="secondary"
                    size="small"
                    sx={{ width: '158px', mr: '16px' }}
                    variant="outlined"
                    onClick={() => handleActionButtonClick('review')}
                  >
                    Review
                  </Button>
                  {getCompanyQuery.data?.result.data.status !== 'rejected' && (
                    <Button
                      color="error"
                      size="small"
                      sx={{
                        width: '173px',
                        mr: getCompanyQuery.data?.result.data.status !== 'closed' ? '16px' : '0px',
                      }}
                      variant="outlined"
                      onClick={() => handleActionButtonClick('reject')}
                    >
                      Reject
                    </Button>
                  )}
                  {getCompanyQuery.data?.result.data.status !== 'closed' && (
                    <Button
                      size="small"
                      sx={{ width: '173px' }}
                      variant="outlined"
                      onClick={() => handleActionButtonClick('close')}
                    >
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <ConfirmModal
          description={`Are you sure you want to ${actionType.current} this company?`}
          loading={isLoadingUpdateCompanyStatusMutation || isLoadingDeleteCompanyMutation}
          open={actionConfirmModalVisible}
          title={`Do you want to ${actionType.current}?`}
          onCancel={hideActionConfirmModal}
          onConfirm={handleActionConfirm}
        />
      </div>
      <FullScreenLoader loading={generatingPdf} />
    </>
  );
};
